import type { HubHistoryEvent } from '@aiai/shared';
import type { HubEffect } from './hubLive';

/** Total length of a replay, in ms. Events are mapped linearly onto it. */
export const REPLAY_MS = 20_000;
/** Two effects on the same planet are at least this far apart. */
export const REPLAY_MIN_GAP_MS = 250;
/** Away for at least this long before a replay is offered. */
export const AWAY_MS = 30 * 60_000;
export const LAST_SEEN_KEY = 'aiai.hub.lastSeenAt';

const HOUR_MS = 3600_000;
const DAY_MS = 24 * HOUR_MS;
/** The history API's maximum look-back: it clamps an older `since` to `until - 7 d`. */
export const HISTORY_MAX_WINDOW_MS = 7 * DAY_MS;

/** Playback periods, each ending now. 7 d is the history API's maximum window. */
export const PLAYBACK_PERIODS = [
  { id: '6h', ms: 6 * HOUR_MS, label: '6 h' },
  { id: '24h', ms: DAY_MS, label: '24 h' },
  { id: '3d', ms: 3 * DAY_MS, label: '3 d' },
  { id: '7d', ms: 7 * DAY_MS, label: '7 d' },
] as const;
export type PlaybackPeriodId = (typeof PLAYBACK_PERIODS)[number]['id'];
export const PLAYBACK_SPEEDS = [1, 2, 3, 4] as const;
export type PlaybackSpeed = (typeof PLAYBACK_SPEEDS)[number];

/** How long a period plays at 1x: one minute per day of history, never shorter than 15 s. */
export function playbackDurationMs(spanMs: number): number {
  // A day (86 400 000 ms) plays in 60 000 ms: 1/1440 of real time.
  return Math.max(15_000, spanMs / 1440);
}

const pad2 = (n: number) => String(n).padStart(2, '0');
const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

/** The replayed moment in local time: `HH:MM` for periods up to 24 h, `ddd HH:MM` (e.g. "Tue 14:05") beyond. */
export function playbackClock(ms: number, spanMs: number): string {
  const d = new Date(ms);
  const hhmm = `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
  return spanMs <= DAY_MS ? hhmm : `${WEEKDAYS[d.getDay()]} ${hhmm}`;
}

export interface ScheduledEvent {
  offsetMs: number;
  event: HubHistoryEvent;
}

interface Slot {
  event: HubHistoryEvent;
  /** Position in time order (ties broken by original index); stable output order. */
  pos: number;
  offset: number;
}

/**
 * Maps history events linearly onto [0, durationMs] by their position in [since, until].
 * Three passes:
 *  1. forward, per planet: push each event later so it is at least minGapMs after the previous
 *     one on that planet, capped at durationMs;
 *  2. backward, per planet: pull each event earlier so it is at least minGapMs before the next
 *     one, so an end-of-window burst fans out backwards instead of stacking on the final frame;
 *     a planet with more events than fit at minGapMs (floor(durationMs / minGapMs) + 1) is the
 *     exception: its events are spread evenly from 0 to durationMs in time order, which breaks
 *     the gap only in that overflow case;
 *  3. sort globally by offset (ties keep time order).
 * Events with an unparsable time are treated as `since`.
 */
export function scheduleReplay(
  events: HubHistoryEvent[],
  since: string,
  until: string,
  durationMs: number = REPLAY_MS,
  minGapMs: number = REPLAY_MIN_GAP_MS,
): ScheduledEvent[] {
  const t0 = Date.parse(since);
  const base = Number.isFinite(t0) ? t0 : 0;
  const span = Date.parse(until) - base;
  const spanOk = Number.isFinite(span) && span > 0;

  const ordered = events
    .map((event, index) => {
      const parsed = Date.parse(event.at);
      return { event, index, t: Number.isFinite(parsed) ? parsed : base };
    })
    .sort((a, b) => a.t - b.t || a.index - b.index);

  const slots: Slot[] = ordered.map(({ event, t }, pos) => {
    const offset = spanOk ? Math.min(Math.max(((t - base) / span) * durationMs, 0), durationMs) : 0;
    return { event, pos, offset: Number.isFinite(offset) ? offset : 0 };
  });

  // Group the time-ordered slots per planet (both passes walk each planet's own sequence).
  const byPlanet = new Map<string, Slot[]>();
  for (const slot of slots) {
    const list = byPlanet.get(slot.event.projectId);
    if (list) list.push(slot);
    else byPlanet.set(slot.event.projectId, [slot]);
  }

  // How many events one planet can hold at the minimum gap, first to last across the window.
  const capacity = minGapMs > 0 ? Math.floor(durationMs / minGapMs) + 1 : Infinity;

  for (const list of byPlanet.values()) {
    if (list.length > capacity) {
      // Overflow: the gap cannot be kept, so spread every event evenly across the full window.
      // Order is preserved and the spacing is as even as integer milliseconds allow.
      const n = list.length;
      list.forEach((slot, i) => {
        slot.offset = Math.round((i * durationMs) / (n - 1));
      });
      continue;
    }
    // Forward: never earlier than the real offset, gap-spaced, capped at the end.
    for (let i = 1; i < list.length; i++) {
      list[i].offset = Math.min(Math.max(list[i].offset, list[i - 1].offset + minGapMs), durationMs);
    }
    // Backward: fan an end-of-window burst out. Each event sits a full gap before its successor
    // where that fits (it always does within capacity), and never after it, so order holds.
    for (let i = list.length - 2; i >= 0; i--) {
      list[i].offset = Math.max(0, Math.min(list[i].offset, list[i + 1].offset - minGapMs));
    }
  }

  return [...slots]
    .sort((a, b) => a.offset - b.offset || a.pos - b.pos)
    .map(({ offset, event }) => ({ offsetMs: offset, event }));
}

/** One line counting what a replay holds: launches, crashes, new missions and runtime errors. */
export function replaySummary(events: HubHistoryEvent[]): { text: string } {
  let launches = 0;
  let crashes = 0;
  let missions = 0;
  let errors = 0;
  for (const e of events) {
    switch (e.type) {
      case 'pr.merged':
        launches++;
        break;
      case 'gate.failed':
      case 'gave.up':
        crashes++;
        break;
      case 'issue.arrived':
        missions++;
        break;
      case 'error.caught':
        errors++;
        break;
    }
  }
  const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
  const parts: string[] = [];
  if (launches) parts.push(plural(launches, 'launch', 'launches'));
  if (crashes) parts.push(plural(crashes, 'crash', 'crashes'));
  if (missions) parts.push(plural(missions, 'new mission', 'new missions'));
  if (errors) parts.push(plural(errors, 'runtime error', 'runtime errors'));
  const text = parts.length ? parts.join(' · ') : 'Quiet while you were away';
  return { text };
}

/** The effect a replayed history event plays on its planet. */
export function effectFor(e: HubHistoryEvent, id: string, at: number): HubEffect | null {
  let kind: HubEffect['kind'];
  switch (e.type) {
    case 'run.started':
      kind = 'comet-out';
      break;
    case 'pr.merged':
      kind = 'comet-home';
      break;
    case 'issue.arrived':
      kind = 'meteor';
      break;
    case 'gate.failed':
    case 'gave.up':
      kind = 'crash';
      break;
    case 'error.caught':
      kind = 'flicker';
      break;
    default:
      return null;
  }
  return { id, kind, projectId: e.projectId, at };
}

/** Reads the last-seen timestamp; a blocked or throwing storage reads as null. */
export function readLastSeen(storage?: Pick<Storage, 'getItem'>): string | null {
  try {
    return (storage ?? globalThis.localStorage).getItem(LAST_SEEN_KEY);
  } catch {
    return null;
  }
}

/** Persists the last-seen timestamp; a blocked or throwing storage is a no-op. */
export function writeLastSeen(at: string, storage?: Pick<Storage, 'setItem'>): void {
  try {
    (storage ?? globalThis.localStorage).setItem(LAST_SEEN_KEY, at);
  } catch {
    // Storage is unavailable (private mode, blocked site data): nothing to persist.
  }
}
